import React from 'react';

const AgreementContent = () => {
    // Palette Colors Reference (same as Terms & Conditions):
    // Dark Text/Headings: #1F2A33
    // Brand Accent: #E84C5B
    // Light Backgrounds: #F6F4F1
    // Border/Muted: #D6D1CB

    return (
        <div className="pt-32 pb-24 px-6 max-w-5xl mx-auto animate-fade-in font-sans text-[#1F2A33]">

            {/* Header */}
            <header className="mb-16 border-b border-[#D6D1CB] pb-10">
                <span className="text-[#E84C5B] font-bold tracking-widest uppercase text-sm mb-4 block">
                    Legal Agreement
                </span>
                <h1 className="text-5xl md:text-6xl font-serif font-bold text-[#1F2A33] mb-6 leading-tight">
                    Booking & Occupancy Agreement
                </h1>
            </header>

            {/* Content */}
            <div className="prose prose-xl prose-slate max-w-none text-[#1F2A33]/80 leading-relaxed">

                {/* Introduction */}
                <p className="lead text-2xl text-[#1F2A33] mb-12">
                    This Booking & Occupancy Agreement (“Agreement”) is entered into by and between the
                    property owner or authorized manager (“Member”), the individual making the reservation
                    (“Guest”), and Another Home, LLC (“AnotherHome”), which is an intended third-party
                    beneficiary of this Agreement solely with respect to provisions that expressly reference it.
                    The residential property identified in the booking confirmation (the “Property”) is made
                    available to Guest for temporary occupancy only for the dates, duration, and number of
                    occupants approved in the booking confirmation (the “Occupancy Period”).
                </p>

                {/* 1. Occupancy and Condition */}
                <section className="mb-14">
                    <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#1F2A33] mb-6">1. Occupancy and Condition</h2>
                    <p>
                        Guest accepts the Property in its existing condition and acknowledges that the Property is
                        provided “AS IS.” Neither the Member nor AnotherHome makes any representation or warranty
                        regarding the condition, suitability, habitability, or fitness of the Property beyond what is
                        expressly stated in this Agreement or required by law.
                    </p>
                </section>

                {/* 2. Guest Representations; Termination */}
                <section className="mb-14">
                    <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#1F2A33] mb-6">2. Guest Representations; Termination</h2>
                    <p>
                        Guest represents and warrants that all information provided in connection with the reservation is
                        accurate and complete, including but not limited to the number of occupants, presence of pets or
                        animals, and the intended use of the Property. The truth and accuracy of these representations are
                        conditions precedent to this Agreement.
                    </p>
                    <p>
                        Any material misrepresentation, violation of house rules, excessive occupancy, unauthorized
                        events, illegal activity, or breach of this Agreement constitutes grounds for immediate
                        termination of the reservation and eviction of all occupants, without refund of any fees paid.
                    </p>
                </section>

                {/* 3. Fees; No Refunds; Chargebacks */}
                <section className="mb-14">
                    <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#1F2A33] mb-6">3. Fees; No Refunds; Chargebacks</h2>
                    <p>
                        All fees identified in the booking confirmation, including rental fees and ancillary charges, are
                        non-refundable except where expressly stated otherwise in writing.
                    </p>
                    <p>
                        Guest agrees not to initiate any chargeback, payment reversal, or payment dispute. Any such
                        action constitutes a material breach of this Agreement. Guest agrees to reimburse Member and/or
                        AnotherHome for all costs, fees, and expenses incurred as a result of any chargeback or payment
                        dispute, including administrative and legal expenses, and authorizes recovery of such amounts by
                        any lawful means.
                    </p>
                    <p>
                        No refunds will be issued due to weather conditions, travel delays, transportation issues, or early
                        departure.
                    </p>
                </section>

                {/* 4. Cancellation Policy */}
                <section className="mb-14">
                    <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#1F2A33] mb-6">4. Cancellation Policy</h2>
                    <p>
                        Unless otherwise expressly stated in writing, the following cancellation policy applies:
                    </p>
                    <p>
                        A cancellation made more than sixty (60) days prior to the start of the Occupancy Period shall receive a one hundred percent (100%) refund of rental fees paid. A cancellation made between sixty (60) and thirty (30) days prior to the start of the Occupancy Period shall receive a fifty percent (50%) refund of rental fees paid. No refunds shall be issued for cancellations made thirty (30) days or less prior to the start of the Occupancy Period. Refunds, if applicable, apply only to rental fees and do not include service fees, transaction fees, or ancillary charges. All cancellations must be received in writing to be effective.
                    </p>
                    <p></p>
                    <p>
                        Refunds, if applicable, apply only to rental fees and do not include service fees, transaction fees, or ancillary charges. Any approved refund shall be issued net of any non‑refundable payment processing fees charged by third‑party payment processors (including, without limitation, Stripe or similar providers) that were incurred at the time of the original booking and are not recoverable by the party issuing the refund.
                    </p>
                </section>

                {/* 5. Rules of Use */}
                <section className="mb-14">
                    <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#1F2A33] mb-6">5. Rules of Use</h2>
                    <p>
                        Guest agrees to comply with all house rules, occupancy limits, noise restrictions, parking rules,
                        and applicable laws and regulations. The Property may not be used for events, parties,
                        commercial purposes, or any unlawful activity.
                    </p>
                    <p>
                        Unauthorized pets, excessive occupancy, or misuse of the Property may result in immediate
                        termination and assessment of additional charges or liquidated damages as disclosed in the
                        booking confirmation or house rules.
                    </p>
                </section>

                {/* 6. Damage; Indemnification */}
                <section className="mb-14">
                    <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#1F2A33] mb-6">6. Damage; Indemnification</h2>
                    <p>
                        Guest is responsible for all damage to the Property caused by Guest or any occupant or visitor.
                        Guest agrees to indemnify, defend, and hold harmless the Member and AnotherHome from any
                        claims, damages, losses, liabilities, costs, or expenses arising from the reservation or occupancy,
                        except to the extent caused by the gross negligence or willful misconduct of the indemnified
                        party.
                    </p>
                </section>

                {/* 7. Limitation of Liability */}
                <section className="mb-14">
                    <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#1F2A33] mb-6">7. Limitation of Liability</h2>
                    <p>
                        To the maximum extent permitted by law, neither the Member nor AnotherHome shall be liable
                        for any indirect, incidental, consequential, or special damages arising out of or relating to the
                        reservation or occupancy of the Property. Guest releases AnotherHome from all claims arising
                        from its role as a booking and marketing intermediary.
                    </p>
                </section>

                {/* 8. Disputes */}
                <section className="mb-14">
                    <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#1F2A33] mb-6">8. Disputes</h2>
                    <p>
                        Any dispute between Guest and Member relating to the reservation or occupancy shall be
                        submitted to mediation prior to the initiation of litigation. AnotherHome shall have no liability
                        for disputes between Guest and Member.
                    </p>
                </section>

                {/* 9. Miscellaneous */}
                <section className="mb-14">
                    <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#1F2A33] mb-6">9. Miscellaneous</h2>
                    <p>
                        This Agreement does not create a landlord-tenant relationship. Guest has no possessory interest in
                        the Property beyond the limited right of temporary occupancy granted herein.
                    </p>
                    <p>
                        This Agreement shall be governed by and construed in accordance with the laws of the State of
                        New York. Exclusive venue for any action arising from this Agreement shall lie in the state or
                        federal courts located in New York County, New York.
                    </p>
                    <p>
                        If any provision of this Agreement is held unenforceable, the remaining provisions shall remain
                        in full force and effect.
                    </p>
                    <p className="font-bold text-[#1F2A33] mt-6">
                        By booking, paying for, or occupying the Property, Guest acknowledges that Guest has read,
                        understood, and agreed to be bound by this Agreement.
                    </p>
                </section>

                {/* Compliance & Local Regulations Box */}
                <section className="mt-16 bg-[#F6F4F1] p-8 md:p-10 rounded-[2rem] border border-[#D6D1CB]">
                    <h3 className="text-2xl md:text-3xl font-serif font-bold text-[#1F2A33] mb-6 flex items-center gap-3">
                        <span className="text-[#E84C5B]">✦</span> Compliance & Local Regulations (New York State)
                    </h3>
                    <div className="space-y-4 text-[1.1rem]">
                        <p>
                            Local laws and regulations governing short‑term and vacation rentals in New York State vary by
                            municipality and may change over time. The information provided in this section is offered for general
                            informational purposes only and is not intended to constitute legal or professional advice.
                        </p>
                        <p>
                            In many New York municipalities, local regulations may affect permitted lengths of stay, occupancy
                            limits, registration or licensing requirements, and certain advertising or listing obligations. Depending on
                            the jurisdiction, these rules may also impact the enforceability of rental agreements or the ability to
                            collect rent through legal proceedings.
                        </p>
                        <p>
                            Reservations made through AnotherHome are confirmed by the individual property owner and may be
                            subject to property‑specific requirements and applicable local laws or regulations. While AnotherHome
                            aims to provide helpful and transparent information, it does not guarantee that any property, listing, or
                            proposed reservation is permitted under all applicable local laws within New York State.
                        </p>
                        <p>
                            Some New York municipalities impose minimum stay requirements, including 14‑night, 30‑night, or
                            longer minimums, or otherwise restrict or regulate short‑term rental activity. Guests and property owners
                            are encouraged to independently review applicable local requirements and consult relevant municipal
                            authorities or qualified legal or tax professionals, as appropriate, prior to booking or offering a property
                            for rent.
                        </p>
                        <p>
                            AnotherHome does not provide legal advice and is not responsible for changes in local regulations or for
                            outcomes resulting from non‑compliance with applicable laws.
                        </p>
                    </div>
                </section>

            </div>
        </div>
    );
};

export default AgreementContent;